import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  TextField,
  makeStyles,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const AiPackageSchema = Yup.object().shape({
  name: Yup.string().required(i18n.t("aiPackages.required")),
  price: Yup.number().required(i18n.t("aiPackages.required")),
  credits: Yup.number().required(i18n.t("aiPackages.required")),
});

const AiPackageModal = ({ open, onClose, aiPackageId, reloadPackages }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    price: 0,
    credits: 0,
    status: true,
  };

  const [aiPackage, setAiPackage] = useState(initialState);

  useEffect(() => {
    const fetchPackage = async () => {
      if (!aiPackageId) return;
      try {
        const { data } = await api.get(`/ai-packages/${aiPackageId}`);
        setAiPackage(data);
      } catch (err) {
        toastError(err);
      }
    };

    fetchPackage();
  }, [aiPackageId, open]);

  const handleClose = () => {
    setAiPackage(initialState);
    onClose();
  };

  const handleSaveAiPackage = async (values) => {
    const packageData = { ...values };
    try {
      if (aiPackageId) {
        await api.put(`/ai-packages/${aiPackageId}`, packageData);
      } else {
        await api.post("/ai-packages", packageData);
      }
      toast.success(i18n.t("aiPackages.packageSaved"));
      reloadPackages();
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {aiPackageId ? i18n.t("aiPackages.editPackage") : i18n.t("aiPackages.addPackageTitle")}
        </DialogTitle>
        <Formik
          initialValues={aiPackage}
          enableReinitialize={true}
          validationSchema={AiPackageSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveAiPackage(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>
                  <Field
                    as={TextField}
                    label={i18n.t("aiPackages.packageName")}
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                  <div style={{ display: "flex", gap: 15 }}>
                    <Field
                      as={TextField}
                      label={i18n.t("aiPackages.priceUsd")}
                      name="price"
                      type="number"
                      error={touched.price && Boolean(errors.price)}
                      helperText={touched.price && errors.price}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                    <Field
                      as={TextField}
                      label={i18n.t("aiPackages.creditsGiven")}
                      name="credits"
                      type="number"
                      error={touched.credits && Boolean(errors.credits)}
                      helperText={touched.credits && errors.credits}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  </div>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.status}
                        onChange={(e) => setFieldValue("status", e.target.checked)}
                        color="primary"
                      />
                    }
                    label={i18n.t("aiPackages.status")}
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("aiPackages.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {aiPackageId ? i18n.t("aiPackages.save") : i18n.t("aiPackages.add")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default AiPackageModal;
